import { currentUser, GoogleCallback, HomeboundUser, signOut } from "@homebound/auth-components";
import { useCallback, useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { AnimatedRoutes } from "~components";
import { Css } from "~generated/css";
import { UserType } from "~generated/graphqlTypes";
import { ConfiguratorLayout, ConfiguratorStep, EnterCode, QrScanPage, SignIn, SignUp, SummaryPrintPage } from "~pages";
import { getStage, shouldUseInternalAuthPool, useInternalPoolKey } from "./context";
import { MaxViewProvider, ToastProvider } from "./contexts";
import { HomeownerConfigurationsLandingPage } from "./pages/configurator/HomeownerConfigurationsLandingPage";
import { InternalConfigurationsLandingPage } from "./pages/configurator/InternalConfigurationsLandingPage";
import { CommunitiesStep } from "./pages/configurator/stepTemplates/Communities.step";
import { LotStep } from "./pages/configurator/stepTemplates/Lot.step";
import { ConfigurationRenderingCapturePage } from "./pages/configuratorRendering/ConfigurationRenderingCapturePage";
import {
  communitiesPath,
  configurationsDownloadPath,
  configurationsLandingPath,
  configuratorPlanBasePath,
  enterCodePath,
  googleCallback,
  lotsPath,
  qrPath,
  signInPath,
  signUpPath,
  unauthorizedUserPath,
  verifyCodePath,
} from "./RouteUrls";
import { UnauthorizedUserTypeLandingPage } from "./UnauthorizedUserTypeLandingPage";
import { setAnalyticsUser } from "./utils/analytics";
import { useCurrentUserLazyQuery } from "./utils/hooks/useCurrentUser.generated";

export default function App() {
  // Note: our auth component currentUser is setup around our cognito pool and does not
  // know about our internal user types defined in blueprint's graphql-service
  const [currentUserLazyQuery] = useCurrentUserLazyQuery();
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<HomeboundUser>();
  const [checkoutEnabledHomeowner, setCheckoutEnabledHomeowner] = useState(false);
  const [isInternal, setIsInternal] = useState(false);

  async function onLoad() {
    const { data: user } = await currentUser();
    setUser(user);
    setIsAuthenticated(user !== undefined);
    const { data: currentUserResult } = await currentUserLazyQuery();
    setCheckoutEnabledHomeowner(currentUserResult?.currentUser?.hasCheckoutAccess ?? false);
    const userIsInternal = currentUserResult?.currentUser?.type === UserType.Internal;
    setIsInternal(userIsInternal);

    // Internal user wrong login flow:
    // We have two cognito user pools (external homeowners & internal users),
    // and currently have to switch our amplify config on-boot to one or the
    // other. We do this based on the user's `@homebound.com` email.
    //
    // However the "sign in with google" SSO link can only work with a single
    // pool, and when rendering the button/link, we don't know which pool to
    // use (b/c the user isn't logged in yet), so we always use the external pool
    // for the best homeowner UX.
    //
    // However if an internal user clicks the "sign in with google", which will
    // work b/c google doesn't care about our cognito pools, they'll come back
    // to the app with google-authed-token from the external pool, but our
    // amply config will be pointing at the internal pool, and so their token
    // will look/be invalid.
    //
    // Here we can detect "oh wait they're an internal user that didn't use
    // the internal pool" and message them about what to do instead.
    if (user && userIsInternal) {
      const stage = getStage();
      const isProd = stage === "prod" || stage === "local-prod";
      const usedInternalLogin = shouldUseInternalAuthPool(isProd);

      if (!usedInternalLogin) {
        sessionStorage.setItem("internalUserWrongLogin", "true");
        await signOut();
        window.location.assign("/signIn");
        return;
      }
    }

    if (currentUserResult?.currentUser) {
      setAnalyticsUser(currentUserResult.currentUser, userIsInternal);
    }

    setIsAuthenticating(false);
  }

  useEffect(() => {
    onLoad().catch((error) => {
      console.error("Failed to load user:", error);
      setIsAuthenticating(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Callback to handle complete authentication flow
  // since hasCheckoutAccess is an additional check we pull from BP
  const onLogin = useCallback(
    async (user: HomeboundUser) => {
      setUser(user);
      const { data } = await currentUserLazyQuery();
      setCheckoutEnabledHomeowner(data?.currentUser?.hasCheckoutAccess ?? false);
      setIsInternal(data?.currentUser?.type === UserType.Internal);
      setIsAuthenticated(true);
    },
    [currentUserLazyQuery],
  );

  // Handle complete sign-out flow
  // because we have an additional check we pull from BP in our routing logic
  // we need to clear everything out at once.
  async function handleSignOut() {
    localStorage.removeItem(useInternalPoolKey);
    await signOut();
    setUser(undefined);
    setIsAuthenticated(false);
    setCheckoutEnabledHomeowner(false);
    setIsInternal(false);
  }

  const authProps = {
    isAuthenticated,
    setIsAuthenticated,
    user,
    setUser,
    signInPath,
    isAuthenticating,
    checkoutEnabledHomeowner,
    isInternal,
  };

  // Regular authenticated routes
  const routes = [
    {
      path: configurationsLandingPath,
      element: isInternal ? (
        <InternalConfigurationsLandingPage handleSignOut={handleSignOut} />
      ) : (
        <HomeownerConfigurationsLandingPage handleSignOut={handleSignOut} />
      ),
      requireAuth: true,
    },
    {
      path: qrPath,
      element: <QrScanPage />,
      requireAuth: true,
    },
    {
      path: communitiesPath,
      element: <ConfiguratorLayout />,
      children: [<Route key="communities" index element={<CommunitiesStep />} />],
      requireAuth: true,
    },
    {
      path: lotsPath,
      element: <ConfiguratorLayout />,
      children: [<Route key="lot" index element={<LotStep />} />],
      requireAuth: true,
    },
    {
      path: configuratorPlanBasePath,
      element: <ConfiguratorLayout />,
      children: [<Route key="step" index element={<ConfiguratorStep />} />],
      requireAuth: true,
    },
    {
      path: configurationsDownloadPath,
      element: <SummaryPrintPage />,
      requireAuth: true,
    },
    {
      path: signInPath,
      element: <SignIn onLogin={onLogin} isInternal={isInternal} />,
      requireAuth: false,
    },
    {
      path: signUpPath,
      element: <SignUp />,
      requireAuth: false,
    },
    {
      path: enterCodePath,
      element: <EnterCode onLogin={onLogin} isInternal={isInternal} />,
      requireAuth: false,
    },
    {
      path: verifyCodePath,
      element: <EnterCode onLogin={onLogin} isInternal={isInternal} />,
      requireAuth: false,
    },
    { path: googleCallback, element: <GoogleCallback />, requireAuth: false },
    {
      path: unauthorizedUserPath,
      element: <UnauthorizedUserTypeLandingPage handleSignOut={handleSignOut} />,
      requireAuth: true,
    },
    { path: "*", element: <Navigate to={configurationsLandingPath} />, requireAuth: true },
  ];

  // RenderingCapture routes can be accessed with just auth cookie (for Puppeteer)
  // These routes don't need ConfiguratorProvider, only MaxViewProvider and ToastProvider
  return (
    <Routes>
      <Route
        path={"/configurations/renderingCapture/:configId/:locationId"}
        element={
          <ToastProvider>
            <MaxViewProvider skipModal={true}>
              {/* For screenshot pages, add container styling to prevent white space at bottom of page */}
              <div css={Css.w100.oh.bgWhite.df.fdc.vh100.$}>
                <Outlet />
              </div>
            </MaxViewProvider>
          </ToastProvider>
        }
      >
        <Route index element={<ConfigurationRenderingCapturePage />} />
      </Route>
      <Route path="*" element={<AnimatedRoutes authProps={authProps}>{routes}</AnimatedRoutes>} />
    </Routes>
  );
}
